/**
 * TimelineMapControls Component
 *
 * Reusable slim-bar controls: a compact timeline sparkline (hover for detail, click
 * to expand to the full fullscreen chart) and a map icon (enabled only when there are
 * report locations, opens the map fullscreen). Operates on the report locations passed
 * in, and reads/writes the shared secondaryDateFilter so timeline drill-down stays in
 * sync with the tab content.
 *
 * Overview feeds it the IRP-wide locations; Precursor/Control feeds it the selected
 * node's locations.
 */

import React, {useCallback, useMemo, useState} from 'react';
import {Badge, Box, Dialog, IconButton, Tooltip, Typography} from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Timeline from './Timeline';
import ClusteredMap from './ClusteredMap';
import FullscreenWrapper from '../../../../../common/FullscreenWrapper';
import {IrpReportLocationDTO} from '../../../../../../@types/integrated-risk-picture';
import {
    useIRPWorkbookStore,
    useSecondaryDateFilter,
} from '../../../../../../stores/irp-workbook.store';

interface TimelineMapControlsProps {
    /** All report locations for the current scope (IRP-wide or a single node). */
    reports: IrpReportLocationDTO[];
}

const TimelineMapControls: React.FC<TimelineMapControlsProps> = ({ reports }) => {
    const secondaryDateFilter = useSecondaryDateFilter();
    const setSecondaryDateFilter = useIRPWorkbookStore((state) => state.setSecondaryDateFilter);
    const [mapOpen, setMapOpen] = useState(false);

    const allLocations = reports;

    // Secondary-date-filtered locations drive the map. All comparisons are UTC.
    const filteredLocations = useMemo((): IrpReportLocationDTO[] => {
        if (allLocations.length === 0) return [];
        if (!secondaryDateFilter?.dateFrom && !secondaryDateFilter?.dateTo) return allLocations;
        return allLocations.filter((loc) => {
            if (!loc.dateTime) return true;
            const date = new Date(loc.dateTime as string);
            if (secondaryDateFilter?.dateFrom && date < secondaryDateFilter.dateFrom) return false;
            if (secondaryDateFilter?.dateTo && date > secondaryDateFilter.dateTo) return false;
            return true;
        });
    }, [allLocations, secondaryDateFilter]);

    // Reports with a real (valid, non-zero) coordinate — drives the map icon's enabled state.
    const locatedReports = useMemo((): IrpReportLocationDTO[] =>
        filteredLocations.filter(
            (loc) => loc.latitude != null && loc.longitude != null && !(loc.latitude === 0 && loc.longitude === 0)
        ),
    [filteredLocations]);
    const hasLocations = locatedReports.length > 0;

    const activeDrillDownYear = secondaryDateFilter?.dateFrom?.getUTCFullYear() ?? null;

    // Year span shown before the sparkline: "2015-2025", or a single year. While
    // drilled into a year, show just that year (the sparkline shows that year's months).
    const yearRangeLabel = useMemo((): string | null => {
        if (activeDrillDownYear != null) return `${activeDrillDownYear}`;
        let min = Infinity;
        let max = -Infinity;
        for (const loc of allLocations) {
            if (!loc.dateTime) continue;
            const year = new Date(loc.dateTime as string).getUTCFullYear();
            if (isNaN(year)) continue;
            if (year < min) min = year;
            if (year > max) max = year;
        }
        if (min === Infinity) return null;
        return min === max ? `${min}` : `${min}-${max}`;
    }, [allLocations, activeDrillDownYear]);

    const handleTimelineBarClick = useCallback((dateFrom: Date, dateTo: Date): void => {
        setSecondaryDateFilter({ dateFrom, dateTo });
    }, [setSecondaryDateFilter]);

    const handleTimelineReset = useCallback((): void => {
        setSecondaryDateFilter(null);
    }, [setSecondaryDateFilter]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Year span summary before the chart */}
            {yearRangeLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {yearRangeLabel}
                </Typography>
            )}

            {/* Timeline sparkline — the whole Timeline lives here; clicking it opens fullscreen */}
            <Box sx={{
                width: 150,
                height: 32,
                position: 'relative',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden'
            }}>
                <FullscreenWrapper fillParent hideExpandButton>
                    <Timeline
                        variant="sparkline"
                        reports={allLocations}
                        onBarClick={handleTimelineBarClick}
                        onReset={handleTimelineReset}
                        activeDrillDownYear={activeDrillDownYear}
                    />
                </FullscreenWrapper>
            </Box>

            {/* Map icon — opens the map fullscreen; disabled when there are no locations */}
            <Tooltip title={hasLocations ? 'View report locations' : 'No report locations'}>
                <span>
                    <IconButton
                        aria-label="Report locations map"
                        size="small"
                        disabled={!hasLocations}
                        onClick={(): void => setMapOpen(true)}
                    >
                        <Badge badgeContent={locatedReports.length} color="primary" max={999}>
                            <MapOutlinedIcon fontSize="small" />
                        </Badge>
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog fullScreen open={mapOpen} onClose={(): void => setMapOpen(false)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ fontWeight: 600 }}>Report Locations</Box>
                        <IconButton aria-label="Close map" onClick={(): void => setMapOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <ClusteredMap reports={filteredLocations} />
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default TimelineMapControls;
